// JCD Music App v2 , assets/jcd-music-app.js
// Mario Paint Composer inspired step-sequencer with JCD twist.
// Horizontal instrument palette, treble-clef staff, beat numbers, progress
// strip, transport bar with title input.

const HEADER_SELECTOR = 'header.jcd-h, [id^="jcd-h-"]';

const INSTRUMENTS = [
  { id: 'mario',    label: 'Mario',  glyph: '🍄', voice: 'piano'  },
  { id: 'star',     label: 'Star',   glyph: '⭐', voice: 'chime'  },
  { id: 'yoshi',    label: 'Yoshi',  glyph: '🦖', voice: 'voice'  },
  { id: 'flower',   label: 'Flower', glyph: '🌸', voice: 'flute'  },
  { id: 'gameboy',  label: 'Chip',   glyph: '🎮', voice: 'square' },
  { id: 'plane',    label: 'Plane',  glyph: '✈️', voice: 'whoosh' },
  { id: 'dog',      label: 'Dog',    glyph: '🐶', voice: 'bark'   },
  { id: 'cat',      label: 'Cat',    glyph: '🐱', voice: 'meow'   },
  { id: 'pig',      label: 'Pig',    glyph: '🐷', voice: 'oink'   },
  { id: 'duck',     label: 'Duck',   glyph: '🦆', voice: 'quack'  },
  { id: 'heart',    label: 'Bell',   glyph: '❤️', voice: 'bell'   },
  { id: 'face',     label: 'Voice',  glyph: '😀', voice: 'baby'   },
];

const ERASER = { id: 'eraser', label: 'Eraser', glyph: '🩹', voice: null };

const SCALE_DEGREES = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const SCALE_SEMITONES = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

function buildPitchTable(rowCount) {
  const out = [];
  let degIndex = 0;
  let octave = 6;
  for (let r = 0; r < rowCount; r += 1) {
    out.push({ note: SCALE_DEGREES[degIndex], octave });
    degIndex -= 1;
    if (degIndex < 0) { degIndex = SCALE_DEGREES.length - 1; octave -= 1; }
  }
  return out;
}

function frequencyOf(pitch) {
  const A4 = 440;
  const semitoneFromA4 =
    SCALE_SEMITONES[pitch.note] - SCALE_SEMITONES.A + (pitch.octave - 4) * 12;
  return A4 * Math.pow(2, semitoneFromA4 / 12);
}

function measureHeaderHeight() {
  const headerEl = document.querySelector(HEADER_SELECTOR);
  const h = headerEl ? Math.round(headerEl.getBoundingClientRect().height) : 0;
  document.documentElement.style.setProperty('--ma-header-h', h + 'px');
}

// --- Pixel-art glyph rendering ------------------------------------------
// Each instrument's emoji is rendered into a tiny canvas (~18px) and then
// upscaled with `image-rendering: pixelated` in CSS, giving every sound
// effect a chunky SNES-Mario-Paint sprite look. Cached per emoji.
const PIXEL_GLYPH_CACHE = new Map();

function getPixelGlyph(emoji) {
  if (PIXEL_GLYPH_CACHE.has(emoji)) return PIXEL_GLYPH_CACHE.get(emoji);
  let result = null;
  try {
    const lowRes = 18;
    const c = document.createElement('canvas');
    c.width = lowRes;
    c.height = lowRes;
    const ctx = c.getContext('2d');
    if (ctx) {
      ctx.imageSmoothingEnabled = false;
      ctx.font = `${Math.floor(lowRes * 0.95)}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(emoji, lowRes / 2, lowRes / 2 + 1);
      // Sanity check: did the emoji actually render? On systems without an
      // emoji font the canvas is empty; fall back to text rendering.
      const data = ctx.getImageData(0, 0, lowRes, lowRes).data;
      let visible = 0;
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] > 20) visible += 1;
        if (visible >= 8) break;
      }
      if (visible >= 8) result = c.toDataURL('image/png');
    }
  } catch (e) {
    result = null;
  }
  PIXEL_GLYPH_CACHE.set(emoji, result);
  return result;
}

function glyphMarkup(emoji, className) {
  const pix = getPixelGlyph(emoji);
  if (pix) {
    const cls = className + ' jcd-ma__stamp-glyph--pixel';
    return `<img class="${cls}" src="${pix}" alt="" aria-hidden="true" />`;
  }
  return `<span class="${className}" aria-hidden="true">${emoji}</span>`;
}

function noteMarkup(emoji) {
  const pix = getPixelGlyph(emoji);
  if (pix) {
    return `<img class="jcd-ma__note-img" src="${pix}" alt="" aria-hidden="true" />`;
  }
  return emoji;
}

// --- Web Audio engine ----------------------------------------------------

class AudioEngine {
  constructor() {
    this.ctx = null;
    this.masterGain = null;
    this.noiseBuffer = null;
  }
  ensure() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0.6;
    this.masterGain.connect(this.ctx.destination);
    this.noiseBuffer = this._makeNoiseBuffer();
  }
  resume() {
    this.ensure();
    if (this.ctx && this.ctx.state === 'suspended') this.ctx.resume();
  }
  _makeNoiseBuffer() {
    const sampleRate = this.ctx.sampleRate;
    const buf = this.ctx.createBuffer(1, sampleRate * 0.5, sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
    return buf;
  }
  trigger(voice, frequency, when) {
    this.ensure();
    if (!this.ctx) return;
    const t = when || this.ctx.currentTime;
    switch (voice) {
      case 'piano':  this._piano(frequency, t);  break;
      case 'chime':  this._chime(frequency, t);  break;
      case 'voice':  this._voice(frequency, t);  break;
      case 'flute':  this._flute(frequency, t);  break;
      case 'square': this._square(frequency, t); break;
      case 'whoosh': this._whoosh(frequency, t); break;
      case 'bark':   this._bark(frequency, t);   break;
      case 'meow':   this._meow(frequency, t);   break;
      case 'oink':   this._oink(frequency, t);   break;
      case 'quack':  this._quack(frequency, t);  break;
      case 'bell':   this._bell(frequency, t);   break;
      case 'baby':   this._baby(frequency, t);   break;
      default: this._piano(frequency, t);
    }
  }
  _envelope(t, peak, attack, release) {
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + attack + release);
    return g;
  }
  _osc(type, freq, t) {
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    return o;
  }
  _piano(freq, t) {
    const o1 = this._osc('triangle', freq, t);
    const o2 = this._osc('sine', freq * 2, t);
    const g  = this._envelope(t, 0.35, 0.005, 0.65);
    const g2 = this._envelope(t, 0.12, 0.005, 0.45);
    o1.connect(g).connect(this.masterGain);
    o2.connect(g2).connect(this.masterGain);
    o1.start(t); o2.start(t);
    o1.stop(t + 0.75); o2.stop(t + 0.55);
  }
  _chime(freq, t) {
    const o1 = this._osc('sine', freq * 2, t);
    const o2 = this._osc('sine', freq * 3, t);
    const g1 = this._envelope(t, 0.25, 0.002, 1.2);
    const g2 = this._envelope(t, 0.12, 0.002, 0.9);
    o1.connect(g1).connect(this.masterGain);
    o2.connect(g2).connect(this.masterGain);
    o1.start(t); o2.start(t);
    o1.stop(t + 1.25); o2.stop(t + 1.0);
  }
  _voice(freq, t) {
    const o = this._osc('triangle', freq, t);
    o.frequency.exponentialRampToValueAtTime(freq * 1.4, t + 0.12);
    const g = this._envelope(t, 0.32, 0.01, 0.18);
    o.connect(g).connect(this.masterGain);
    o.start(t);
    o.stop(t + 0.22);
  }
  _flute(freq, t) {
    const o = this._osc('sine', freq, t);
    const lfo = this._osc('sine', 5, t);
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = freq * 0.01;
    lfo.connect(lfoGain).connect(o.frequency);
    const g = this._envelope(t, 0.28, 0.04, 0.5);
    o.connect(g).connect(this.masterGain);
    o.start(t); lfo.start(t);
    o.stop(t + 0.6); lfo.stop(t + 0.6);
  }
  _square(freq, t) {
    const o = this._osc('square', freq, t);
    const g = this._envelope(t, 0.18, 0.005, 0.22);
    o.connect(g).connect(this.masterGain);
    o.start(t);
    o.stop(t + 0.3);
  }
  _whoosh(freq, t) {
    const src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(freq * 2, t);
    filter.frequency.exponentialRampToValueAtTime(freq * 4, t + 0.25);
    filter.Q.value = 2;
    const g = this._envelope(t, 0.3, 0.01, 0.35);
    src.connect(filter).connect(g).connect(this.masterGain);
    src.start(t);
    src.stop(t + 0.4);
  }
  _bark(freq, t) {
    const o = this._osc('sawtooth', freq * 0.5, t);
    o.frequency.exponentialRampToValueAtTime(freq * 0.7, t + 0.05);
    o.frequency.exponentialRampToValueAtTime(freq * 0.4, t + 0.18);
    const g = this._envelope(t, 0.4, 0.005, 0.2);
    o.connect(g).connect(this.masterGain);
    o.start(t);
    o.stop(t + 0.25);
  }
  _meow(freq, t) {
    const o = this._osc('triangle', freq * 0.9, t);
    o.frequency.exponentialRampToValueAtTime(freq * 1.1, t + 0.12);
    o.frequency.exponentialRampToValueAtTime(freq * 0.8, t + 0.32);
    const g = this._envelope(t, 0.3, 0.02, 0.35);
    o.connect(g).connect(this.masterGain);
    o.start(t);
    o.stop(t + 0.4);
  }
  _oink(freq, t) {
    const o = this._osc('sawtooth', freq * 0.4, t);
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = freq * 1.2;
    filter.Q.value = 4;
    const g = this._envelope(t, 0.34, 0.01, 0.2);
    o.connect(filter).connect(g).connect(this.masterGain);
    o.start(t);
    o.stop(t + 0.25);
  }
  _quack(freq, t) {
    const o = this._osc('square', freq * 0.6, t);
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = freq;
    filter.Q.value = 6;
    const g = this._envelope(t, 0.36, 0.005, 0.16);
    o.connect(filter).connect(g).connect(this.masterGain);
    o.start(t);
    o.stop(t + 0.2);
  }
  _bell(freq, t) {
    const o1 = this._osc('sine', freq, t);
    const o2 = this._osc('sine', freq * 2.756, t);
    const g1 = this._envelope(t, 0.22, 0.002, 1.4);
    const g2 = this._envelope(t, 0.1,  0.002, 1.0);
    o1.connect(g1).connect(this.masterGain);
    o2.connect(g2).connect(this.masterGain);
    o1.start(t); o2.start(t);
    o1.stop(t + 1.45); o2.stop(t + 1.05);
  }
  _baby(freq, t) {
    const o = this._osc('sine', freq, t);
    o.frequency.exponentialRampToValueAtTime(freq * 1.1, t + 0.08);
    o.frequency.exponentialRampToValueAtTime(freq * 0.95, t + 0.22);
    const g = this._envelope(t, 0.28, 0.02, 0.28);
    o.connect(g).connect(this.masterGain);
    o.start(t);
    o.stop(t + 0.35);
  }
}

// --- Intro patterns ------------------------------------------------------
function introNotes(name, rows, steps) {
  if (name === 'empty') return [];

  const rowFor = (note, octave) => {
    let degIndex = 0, oct = 6;
    for (let r = 0; r < rows; r += 1) {
      if (SCALE_DEGREES[degIndex] === note && oct === octave) return r;
      degIndex -= 1;
      if (degIndex < 0) { degIndex = SCALE_DEGREES.length - 1; oct -= 1; }
    }
    return -1;
  };

  if (name === 'scale') {
    const out = [];
    let col = 0;
    for (let r = rows - 1; r >= 0; r -= 1) {
      if (col >= steps) break;
      out.push([col, r, 'mario']);
      col += 2;
    }
    return out;
  }

  if (name === 'mario') {
    const m = [
      ['E', 5, 0], ['E', 5, 2], ['E', 5, 5],
      ['C', 5, 7], ['E', 5, 9],
      ['G', 5, 12], ['G', 4, 18],
    ];
    return m
      .map(([n, o, c]) => [c, rowFor(n, o), 'mario'])
      .filter(([col, row]) => col < steps && row >= 0);
  }

  // twinkle (default)
  const t = [
    ['C', 5, 0],  ['C', 5, 1],
    ['G', 5, 2],  ['G', 5, 3],
    ['A', 5, 4],  ['A', 5, 5],
    ['G', 5, 6],
    ['F', 5, 8],  ['F', 5, 9],
    ['E', 5, 10], ['E', 5, 11],
    ['D', 5, 12], ['D', 5, 13],
    ['C', 5, 14],
  ];
  return t
    .map(([n, o, c]) => [c, rowFor(n, o), 'star'])
    .filter(([col, row]) => col < steps && row >= 0);
}

// --- App -----------------------------------------------------------------

function init(sectionEl) {
  const steps = Number(sectionEl.dataset.steps || 24);
  const rows  = Number(sectionEl.dataset.rows  || 13);
  const bpmDefault = Number(sectionEl.dataset.bpmDefault || 120);
  const bpmMin = Number(sectionEl.dataset.bpmMin || 40);
  const bpmMax = Number(sectionEl.dataset.bpmMax || 300);
  const loopDefault = sectionEl.dataset.loopDefault !== 'false';
  const introPattern = sectionEl.dataset.introPattern || 'twinkle';
  const trackTitleDefault = sectionEl.dataset.trackTitleDefault || 'BREAK THE CHAIN';

  const idSuffix = sectionEl.id.replace(/^jcd-ma-/, '');
  const toolbarEl   = sectionEl.querySelector(`#jcd-ma-toolbar-${idSuffix}`);
  const stageEl     = sectionEl.querySelector(`#jcd-ma-stage-${idSuffix}`);
  const staffEl     = sectionEl.querySelector(`#jcd-ma-staff-${idSuffix}`);
  const beatnumsEl  = sectionEl.querySelector(`#jcd-ma-beatnums-${idSuffix}`);
  const progressEl  = sectionEl.querySelector(`#jcd-ma-progress-${idSuffix}`);
  const playheadEl  = sectionEl.querySelector(`#jcd-ma-playhead-${idSuffix}`);
  const statusEl    = sectionEl.querySelector(`#jcd-ma-status-${idSuffix}`);

  const pitches = buildPitchTable(rows);
  const engine = new AudioEngine();

  const notes = Array.from({ length: steps }, () => new Array(rows).fill(null));

  const state = {
    selectedInstrument: 'mario',
    bpm: bpmDefault,
    loop: loopDefault,
    playing: false,
    currentStep: -1,
    nextStepTime: 0,
    lookaheadTimer: null,
    trackTitle: trackTitleDefault,
  };

  function renderPalette() {
    toolbarEl.innerHTML = '';
    const all = [...INSTRUMENTS, ERASER];
    // grid-template-columns is owned by CSS so mobile media query can override it
    all.forEach((inst) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'jcd-ma__stamp' + (state.selectedInstrument === inst.id ? ' is-active' : '');
      if (inst.id === 'eraser') btn.classList.add('jcd-ma__stamp--eraser');
      btn.dataset.instrument = inst.id;
      btn.setAttribute('aria-label', inst.label);
      btn.innerHTML = `${glyphMarkup(inst.glyph, 'jcd-ma__stamp-glyph')}<span class="jcd-ma__stamp-label">${inst.label}</span>`;
      btn.addEventListener('click', () => {
        state.selectedInstrument = inst.id;
        renderPalette();
        engine.resume();
        if (inst.id !== 'eraser') {
          engine.trigger(inst.voice, frequencyOf({ note: 'C', octave: 5 }));
        }
      });
      toolbarEl.appendChild(btn);
    });
  }

  function renderBeatnums() {
    beatnumsEl.innerHTML = '';
    beatnumsEl.style.gridTemplateColumns = `repeat(${steps}, 1fr)`;
    for (let c = 0; c < steps; c += 1) {
      const cell = document.createElement('span');
      cell.className = 'jcd-ma__beatnums-cell';
      if ((c + 1) % 4 === 0) {
        const beat = Math.floor((c + 1) / 4);
        cell.textContent = String(beat);
        if ((c + 1) % 8 === 0) cell.classList.add('is-bar');
      }
      beatnumsEl.appendChild(cell);
    }
  }

  function renderProgress() {
    progressEl.innerHTML = '';
    progressEl.style.gridTemplateColumns = `repeat(${steps}, 1fr)`;
    for (let c = 0; c < steps; c += 1) {
      const m = document.createElement('span');
      m.className = 'jcd-ma__progress-marker';
      if ((c + 1) % 8 === 0) m.classList.add('is-bar');
      progressEl.appendChild(m);
    }
    const head = document.createElement('div');
    head.className = 'jcd-ma__progress-head';
    head.id = `jcd-ma-progress-head-${idSuffix}`;
    progressEl.appendChild(head);
  }

  function renderStaff() {
    staffEl.innerHTML = '';
    staffEl.style.gridTemplateColumns = `repeat(${steps}, 1fr)`;
    staffEl.style.gridTemplateRows = `repeat(${rows}, 1fr)`;

    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < steps; c += 1) {
        const cell = document.createElement('div');
        cell.className = 'jcd-ma__cell';
        if ((c + 1) % 4 === 0 && c !== steps - 1) cell.classList.add('is-beat-col');
        if ((c + 1) % 8 === 0 && c !== steps - 1) cell.classList.add('is-bar-col');
        cell.dataset.col = String(c);
        cell.dataset.row = String(r);
        cell.style.gridColumn = String(c + 1);
        cell.style.gridRow = String(r + 1);
        cell.addEventListener('pointerdown', onCellPointerDown);
        staffEl.appendChild(cell);
      }
    }
    repaintNotes();
  }

  function repaintNotes() {
    staffEl.querySelectorAll('.jcd-ma__note').forEach((n) => n.remove());
    for (let c = 0; c < steps; c += 1) {
      for (let r = 0; r < rows; r += 1) {
        const id = notes[c][r];
        if (!id) continue;
        const inst = INSTRUMENTS.find((i) => i.id === id);
        if (!inst) continue;
        const cell = staffEl.querySelector(`.jcd-ma__cell[data-col="${c}"][data-row="${r}"]`);
        if (!cell) continue;
        const noteEl = document.createElement('span');
        noteEl.className = 'jcd-ma__note';
        noteEl.dataset.col = String(c);
        noteEl.dataset.row = String(r);
        noteEl.dataset.instrument = id;
        noteEl.innerHTML = noteMarkup(inst.glyph);
        cell.appendChild(noteEl);
      }
    }
  }

  function onCellPointerDown(e) {
    e.preventDefault();
    engine.resume();
    const cell = e.currentTarget;
    const c = Number(cell.dataset.col);
    const r = Number(cell.dataset.row);
    if (state.selectedInstrument === 'eraser') {
      notes[c][r] = null;
    } else {
      const existing = notes[c][r];
      if (existing === state.selectedInstrument) {
        notes[c][r] = null;
      } else {
        notes[c][r] = state.selectedInstrument;
        const inst = INSTRUMENTS.find((i) => i.id === state.selectedInstrument);
        if (inst) engine.trigger(inst.voice, frequencyOf(pitches[r]));
      }
    }
    repaintNotes();
  }

  function renderTransport() {
    statusEl.innerHTML = '';

    const trio = document.createElement('div');
    trio.className = 'jcd-ma__transport-group';

    const stopBtn = document.createElement('button');
    stopBtn.type = 'button';
    stopBtn.className = 'jcd-ma__btn';
    stopBtn.innerHTML = '<span class="jcd-ma__btn-glyph" aria-hidden="true">■</span>STOP';
    stopBtn.addEventListener('click', () => {
      stop();
      refreshPlayState();
    });
    trio.appendChild(stopBtn);

    const playBtn = document.createElement('button');
    playBtn.type = 'button';
    playBtn.className = 'jcd-ma__btn' + (state.playing ? ' is-pressed' : '');
    playBtn.dataset.role = 'play';
    playBtn.innerHTML = '<span class="jcd-ma__btn-glyph" aria-hidden="true">▶</span>PLAY';
    playBtn.addEventListener('click', () => {
      engine.resume();
      if (state.playing) stop(); else play();
      refreshPlayState();
    });
    trio.appendChild(playBtn);

    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'jcd-ma__btn';
    clearBtn.innerHTML = '<span class="jcd-ma__btn-glyph" aria-hidden="true">★</span>CLEAR';
    clearBtn.addEventListener('click', openClearModal);
    trio.appendChild(clearBtn);

    statusEl.appendChild(trio);

    const loopBtn = document.createElement('button');
    loopBtn.type = 'button';
    loopBtn.className = 'jcd-ma__btn' + (state.loop ? ' is-on' : '');
    loopBtn.innerHTML = '<span class="jcd-ma__btn-glyph" aria-hidden="true">↻</span>LOOP';
    loopBtn.addEventListener('click', () => {
      state.loop = !state.loop;
      loopBtn.classList.toggle('is-on', state.loop);
    });
    statusEl.appendChild(loopBtn);

    const bpmWrap = document.createElement('div');
    bpmWrap.className = 'jcd-ma__bpm';
    bpmWrap.innerHTML = `
      <span class="jcd-ma__bpm-label">BPM</span>
      <span class="jcd-ma__bpm-value">${state.bpm}</span>
      <input type="range" min="${bpmMin}" max="${bpmMax}" value="${state.bpm}" step="1" aria-label="BPM" />
    `;
    const bpmInput = bpmWrap.querySelector('input');
    const bpmValue = bpmWrap.querySelector('.jcd-ma__bpm-value');
    bpmInput.addEventListener('input', () => {
      state.bpm = Number(bpmInput.value);
      bpmValue.textContent = String(state.bpm);
    });
    statusEl.appendChild(bpmWrap);

    const titleField = document.createElement('div');
    titleField.className = 'jcd-ma__title-field';
    titleField.innerHTML = `
      <span class="jcd-ma__title-label">TITLE</span>
      <input type="text" class="jcd-ma__title-input" maxlength="48" value="${state.trackTitle}" aria-label="Track title" />
    `;
    const titleInput = titleField.querySelector('input');
    titleInput.addEventListener('input', () => {
      state.trackTitle = titleInput.value;
    });
    statusEl.appendChild(titleField);
  }

  function refreshPlayState() {
    const playBtn = statusEl.querySelector('[data-role="play"]');
    if (!playBtn) return;
    playBtn.innerHTML = state.playing
      ? '<span class="jcd-ma__btn-glyph" aria-hidden="true">⏸</span>PAUSE'
      : '<span class="jcd-ma__btn-glyph" aria-hidden="true">▶</span>PLAY';
    playBtn.classList.toggle('is-pressed', state.playing);
  }

  function play() {
    engine.ensure();
    if (!engine.ctx) return;
    state.playing = true;
    state.currentStep = -1;
    state.nextStepTime = engine.ctx.currentTime + 0.04;
    playheadEl.classList.add('is-on');
    tick();
  }

  function stop() {
    state.playing = false;
    if (state.lookaheadTimer) {
      clearTimeout(state.lookaheadTimer);
      state.lookaheadTimer = null;
    }
    playheadEl.classList.remove('is-on');
    playheadEl.style.transform = 'translateX(-100px)';
    const progHead = sectionEl.querySelector(`#jcd-ma-progress-head-${idSuffix}`);
    if (progHead) progHead.style.transform = 'translateX(-100px)';
    staffEl.querySelectorAll('.is-active-col').forEach((c) => c.classList.remove('is-active-col'));
  }

  function tick() {
    if (!state.playing) return;
    const stepDur = 60 / state.bpm / 2;
    const lookahead = 0.1;
    while (state.nextStepTime < engine.ctx.currentTime + lookahead) {
      const step = (state.currentStep + 1) % steps;
      scheduleStep(step, state.nextStepTime);
      state.currentStep = step;
      state.nextStepTime += stepDur;
      if (step === steps - 1 && !state.loop) {
        const stopAt = state.nextStepTime;
        const delayMs = Math.max(0, (stopAt - engine.ctx.currentTime) * 1000);
        setTimeout(() => {
          if (state.playing) {
            stop();
            refreshPlayState();
          }
        }, delayMs + 20);
        break;
      }
    }
    visualPlayhead();
    state.lookaheadTimer = setTimeout(tick, 25);
  }

  function scheduleStep(step, when) {
    for (let r = 0; r < rows; r += 1) {
      const id = notes[step][r];
      if (!id) continue;
      const inst = INSTRUMENTS.find((i) => i.id === id);
      if (!inst) continue;
      engine.trigger(inst.voice, frequencyOf(pitches[r]), when);
    }
  }

  function visualPlayhead() {
    if (!staffEl.firstChild) return;
    const staffRect = staffEl.getBoundingClientRect();
    const stageRect = stageEl.getBoundingClientRect();
    const colWidth = staffRect.width / steps;
    const xInStage = (staffRect.left - stageRect.left) + colWidth * (state.currentStep + 0.5);
    playheadEl.style.transform = `translateX(${xInStage}px)`;

    const progHead = sectionEl.querySelector(`#jcd-ma-progress-head-${idSuffix}`);
    if (progHead && progressEl) {
      const progStyle = getComputedStyle(progressEl);
      const padLeft  = parseFloat(progStyle.paddingLeft)  || 0;
      const padRight = parseFloat(progStyle.paddingRight) || 0;
      const innerW = progressEl.clientWidth - padLeft - padRight;
      const xInProg = padLeft + (innerW / steps) * (state.currentStep + 0.5);
      progHead.style.transform = `translateX(${xInProg}px)`;
    }

    staffEl.querySelectorAll('.is-active-col').forEach((c) => c.classList.remove('is-active-col'));
    staffEl
      .querySelectorAll(`.jcd-ma__cell[data-col="${state.currentStep}"]`)
      .forEach((c) => c.classList.add('is-active-col'));

    staffEl
      .querySelectorAll(`.jcd-ma__note[data-col="${state.currentStep}"]`)
      .forEach((n) => {
        n.classList.remove('is-ringing');
        void n.offsetWidth;
        n.classList.add('is-ringing');
      });
  }

  function openClearModal() {
    const overlay = document.createElement('div');
    overlay.className = 'jcd-ma__modal';
    overlay.innerHTML = `
      <div class="jcd-ma__modal-window">
        <div class="jcd-ma__modal-titlebar">${sectionEl.dataset.modalText || 'Clear all notes?'}</div>
        <div class="jcd-ma__modal-body">
          <p class="jcd-ma__modal-text">This erases every note on the staff.</p>
          <div class="jcd-ma__modal-actions">
            <button type="button" class="jcd-ma__btn" data-action="cancel">${sectionEl.dataset.modalCancel || 'Cancel'}</button>
            <button type="button" class="jcd-ma__btn" data-action="ok">${sectionEl.dataset.modalOk || 'OK'}</button>
          </div>
        </div>
      </div>
    `;
    overlay.addEventListener('click', (e) => {
      const action = e.target.dataset && e.target.dataset.action;
      if (action === 'ok') {
        for (let c = 0; c < steps; c += 1) for (let r = 0; r < rows; r += 1) notes[c][r] = null;
        repaintNotes();
        overlay.remove();
      } else if (action === 'cancel' || e.target === overlay) {
        overlay.remove();
      }
    });
    document.body.appendChild(overlay);
  }

  const maximizeBtn = sectionEl.querySelector('.jcd-ma__win-btn--maximize');
  if (maximizeBtn) {
    const isFs = () => document.fullscreenElement || document.webkitFullscreenElement || null;
    const enter = () => {
      const req = sectionEl.requestFullscreen || sectionEl.webkitRequestFullscreen;
      if (req) {
        const r = req.call(sectionEl);
        if (r && r.catch) r.catch((err) => console.warn('JCD music: fullscreen failed', err));
      }
    };
    const exit = () => {
      const ex = document.exitFullscreen || document.webkitExitFullscreen;
      if (ex) ex.call(document);
    };
    maximizeBtn.addEventListener('click', () => { if (isFs()) exit(); else enter(); });
  }

  const resizeObserver = new ResizeObserver(() => {
    if (state.playing) visualPlayhead();
  });
  resizeObserver.observe(stageEl);

  sectionEl.querySelectorAll('.jcd-ma__tab').forEach((tab) => {
    tab.addEventListener('click', () => {
      sectionEl.querySelectorAll('.jcd-ma__tab').forEach((t) => t.classList.remove('is-active'));
      tab.classList.add('is-active');
    });
  });

  const seed = introNotes(introPattern, rows, steps);
  seed.forEach(([c, r, id]) => { if (c >= 0 && c < steps && r >= 0 && r < rows) notes[c][r] = id; });

  renderPalette();
  renderBeatnums();
  renderStaff();
  renderProgress();
  renderTransport();
}

function boot() {
  measureHeaderHeight();
  document.querySelectorAll('.jcd-ma').forEach(init);

  window.addEventListener('resize', measureHeaderHeight);
  const headerEl = document.querySelector(HEADER_SELECTOR);
  if (headerEl && 'ResizeObserver' in window) {
    new ResizeObserver(measureHeaderHeight).observe(headerEl);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
